import React from 'react';
import {
  CalendarPlus,
  CalendarClock,
  CalendarX,
  UserX,
  Ban,
  RefreshCw,
  UserPlus,
  UserMinus,
  Mail,
  Send,
  BadgeCheck,
  MessageSquare,
  Phone,
  Layers,
  CircleDot,
  Settings,
  CheckCircle2,
  XCircle,
  Users,
  MapPin,
  DollarSign,
  Clock,
  Pen,
  Trash2,
  Upload,
  RotateCcw,
  type LucideIcon,
} from 'lucide-react';
import {Avatar, AvatarFallback} from '@/shared/ui/avatar';
import {Badge} from '@/shared/ui/badge';
import type {HistoryEntry, HistoryChange} from '@/shared/types/history';

/**
 * Shared, presentational change-log timeline. Feed it raw history entries; the
 * same component backs both the participant ("View History") and the session
 * ("Session History") tabs — only the data source differs.
 *
 * Layout: a By-actor filter (All / By Users / By System), then entries grouped
 * by day on a connected timeline rail. Each event leads with a colour-coded
 * action icon (so a lifecycle is scannable at a glance), followed by the actor,
 * old→new value chips, delivery status and an optional reason.
 */
export interface HistoryTimelineProps {
  entries: HistoryEntry[];
  isLoading?: boolean;
  isError?: boolean;
  /** 'participant' (default) or 'session' — affects empty copy only. */
  mode?: 'participant' | 'session';
  emptyLabel?: string;
  /** Show the All / By Users / By System filter chips (default true). */
  showFilter?: boolean;
  /**
   * IANA zone for timestamp values inside change chips, shown with its
   * abbreviation. Defaults to the viewer's local zone.
   */
  timeZone?: string;
}

type ActorFilter = 'all' | 'users' | 'system';

const ACTION_LABELS: Record<string, string> = {
  booking_created: 'Session booked',
  rescheduled: 'Session rescheduled',
  cancelled: 'Session cancelled',
  no_show: 'Marked as no-show',
  called_off: 'Marked as called off',
  moderator_assigned: 'Moderator assigned',
  moderator_removed: 'Moderator removed',
  status_changed: 'Status changed',
  status_change: 'Status changed',
  payment_approval: 'Payment approved',
  contact_attempt: 'Contact attempt',
  note_added: 'Note added',
  participant_created: 'Participant created',
  participant_removed: 'Participant removed',
  endorsed: 'Endorsed',
  invite_sent: 'Self-booking invite sent',
  session_disqualified: 'Disqualified',
  session_do_not_hire: 'Marked do not hire',
  session_overbooked: 'Session overbooked',
  email_sent: 'Email sent',
  // OD-1018: Team / Payment Approvals / Email Setup history tabs.
  team_member_added: 'Member added',
  team_member_removed: 'Member removed',
  team_role_changed: 'Role changed',
  team_status_changed: 'Status changed',
  email_template_updated: 'Template updated',
  email_template_bulk_uploaded: 'Templates bulk uploaded',
  payment_created: 'Payment created',
  payment_edited: 'Payment details edited',
  payment_approval_status_changed: 'Approval status changed',
  payment_status_changed: 'Payment status changed',
  payment_retry: 'Payment retried',
  actual_times_adjusted: 'Session actual times adjusted',
};

/** Icon + colour tone per action type — the timeline's primary scan cue. */
type Tone = 'emerald' | 'amber' | 'rose' | 'orange' | 'blue' | 'violet' | 'slate';

const TONE_CLASS: Record<Tone, string> = {
  emerald:
    'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400',
  amber: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400',
  rose: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-400',
  orange:
    'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-400',
  blue: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400',
  violet:
    'bg-violet-100 text-violet-700 dark:bg-violet-500/15 dark:text-violet-400',
  slate: 'bg-muted text-muted-foreground',
};

const ACTION_VISUAL: Record<string, {icon: LucideIcon; tone: Tone}> = {
  booking_created: {icon: CalendarPlus, tone: 'emerald'},
  participant_created: {icon: UserPlus, tone: 'emerald'},
  participant_removed: {icon: UserMinus, tone: 'rose'},
  project_created: {icon: CalendarPlus, tone: 'emerald'},
  project_delete_requested: {icon: Trash2, tone: 'rose'},
  endorsed: {icon: BadgeCheck, tone: 'emerald'},
  rescheduled: {icon: CalendarClock, tone: 'amber'},
  session_overbooked: {icon: Layers, tone: 'amber'},
  cancelled: {icon: CalendarX, tone: 'rose'},
  called_off: {icon: Ban, tone: 'rose'},
  session_disqualified: {icon: Ban, tone: 'rose'},
  session_do_not_hire: {icon: Ban, tone: 'rose'},
  no_show: {icon: UserX, tone: 'orange'},
  status_changed: {icon: RefreshCw, tone: 'blue'},
  status_change: {icon: RefreshCw, tone: 'blue'},
  moderator_assigned: {icon: UserPlus, tone: 'blue'},
  moderator_removed: {icon: UserMinus, tone: 'slate'},
  invite_sent: {icon: Send, tone: 'violet'},
  email_sent: {icon: Mail, tone: 'slate'},
  note_added: {icon: MessageSquare, tone: 'slate'},
  contact_attempt: {icon: Phone, tone: 'slate'},
  payment_approval: {icon: BadgeCheck, tone: 'emerald'},
  // OD-892: session type + moderator availability history entries.
  moderator_availability_updated: {icon: CalendarClock, tone: 'amber'},
  moderator_availability_approved: {icon: BadgeCheck, tone: 'emerald'},
  moderator_availability_rejected: {icon: XCircle, tone: 'rose'},
  // OD-1018: Team / Payment Approvals / Email Setup history tabs.
  team_member_added: {icon: UserPlus, tone: 'emerald'},
  team_member_removed: {icon: UserMinus, tone: 'slate'},
  team_role_changed: {icon: RefreshCw, tone: 'blue'},
  team_status_changed: {icon: RefreshCw, tone: 'blue'},
  email_template_updated: {icon: Pen, tone: 'slate'},
  email_template_bulk_uploaded: {icon: Upload, tone: 'violet'},
  payment_created: {icon: DollarSign, tone: 'emerald'},
  payment_edited: {icon: Pen, tone: 'amber'},
  payment_approval_status_changed: {icon: RefreshCw, tone: 'blue'},
  payment_status_changed: {icon: RefreshCw, tone: 'amber'},
  payment_retry: {icon: RotateCcw, tone: 'amber'},
  actual_times_adjusted: {icon: Clock, tone: 'amber'},
};

/**
 * session_type_updated entries all share one action_type but cover several
 * distinct edits (enable/disable, moderators, meeting point, date range) —
 * so the icon is picked from which field actually changed, not the action
 * type alone. Status wins when present (an enable/disable is the headline
 * change even if moderators changed in the same edit, e.g. auto-enable).
 */
const SESSION_TYPE_UPDATED = 'session_type_updated';

const sessionTypeVisual = (entry: HistoryEntry): {icon: LucideIcon; tone: Tone} => {
  const changes = entry.metadata?.changes ?? [];
  const byField = (f: string) => changes.find((c) => c.field === f);
  const status = byField('status');
  if (status) {
    return status.to === 'Enabled'
      ? {icon: CheckCircle2, tone: 'emerald'}
      : {icon: XCircle, tone: 'rose'};
  }
  if (byField('moderators')) return {icon: Users, tone: 'blue'};
  if (byField('meeting_point')) return {icon: MapPin, tone: 'violet'};
  if (byField('start_date') || byField('end_date')) return {icon: CalendarClock, tone: 'amber'};
  return {icon: Settings, tone: 'slate'};
};

/**
 * Infer an icon from free text — used for legacy/generic status-change entries
 * whose action_type is just "status_changed" but whose description (or the
 * status they changed to) tells us what really happened (e.g. "Session booked",
 * "Session no show", "… to Reschedule").
 */
const inferVisualFromText = (
  text: string,
): {icon: LucideIcon; tone: Tone} | null => {
  const t = text.toLowerCase();
  if (/no[\s-]?show/.test(t)) return {icon: UserX, tone: 'orange'};
  if (/call(ed)?[\s-]?off/.test(t)) return {icon: Ban, tone: 'rose'};
  if (/cancel/.test(t)) return {icon: CalendarX, tone: 'rose'};
  if (/reschedul/.test(t)) return {icon: CalendarClock, tone: 'amber'};
  if (/disqualif/.test(t)) return {icon: Ban, tone: 'rose'};
  if (/do[\s-]?not[\s-]?hire/.test(t)) return {icon: Ban, tone: 'rose'};
  if (/overbook/.test(t)) return {icon: Layers, tone: 'amber'};
  if (/complet/.test(t)) return {icon: BadgeCheck, tone: 'emerald'};
  if (/book/.test(t)) return {icon: CalendarPlus, tone: 'emerald'};
  if (/endors/.test(t)) return {icon: BadgeCheck, tone: 'emerald'};
  if (/moderator/.test(t)) return {icon: UserPlus, tone: 'blue'};
  if (/invite/.test(t)) return {icon: Send, tone: 'violet'};
  if (/in[\s-]?progress|started/.test(t)) return {icon: RefreshCw, tone: 'blue'};
  return null;
};

const STATUS_CHANGE_TYPES = new Set(['status_changed', 'status_change']);

/**
 * Icon per changed field for a `project_updated` entry — one action type
 * covers several distinct edits (compensation, location, dates, …), so the
 * icon is picked from which field actually changed rather than the type.
 */
const projectFieldVisual = (entry: HistoryEntry): {icon: LucideIcon; tone: Tone} => {
  const field = entry.metadata?.changes?.[0]?.field;
  if (field === 'compensation') return {icon: DollarSign, tone: 'emerald'};
  if (field === 'project_dates') return {icon: CalendarClock, tone: 'amber'};
  if (field === 'time_commitment') return {icon: Clock, tone: 'blue'};
  if (field === 'name') return {icon: Pen, tone: 'slate'};
  if (field === 'description') return {icon: Pen, tone: 'slate'};
  // Location carries no `changes` chip (rendered as a single reason box) —
  // fall back to the label text.
  const label = actionLabel(entry).toLowerCase();
  if (label.includes('location')) return {icon: MapPin, tone: 'violet'};
  if (label.includes('description')) return {icon: Pen, tone: 'slate'};
  return {icon: Settings, tone: 'slate'};
};

const actionVisual = (entry: HistoryEntry): {icon: LucideIcon; tone: Tone} => {
  const type = entry.action_type;
  if (type === SESSION_TYPE_UPDATED) return sessionTypeVisual(entry);
  if (type === 'project_updated') return projectFieldVisual(entry);
  const known = type ? ACTION_VISUAL[type] : undefined;
  const isGenericStatus = Boolean(type && STATUS_CHANGE_TYPES.has(type));

  // A specific action type wins outright (booking_created, cancelled, …).
  if (known && !isGenericStatus) return known;

  // Otherwise infer from the description + the status being changed to, so a
  // generic status-change entry still gets a meaningful icon.
  const statusChange = Array.isArray(entry.metadata?.changes)
    ? entry.metadata!.changes!.find((c) => /status/i.test(c.field))
    : undefined;
  const inferred = inferVisualFromText(
    `${actionLabel(entry)} ${statusChange?.to ?? ''}`,
  );
  if (inferred) return inferred;

  if (isGenericStatus) return {icon: RefreshCw, tone: 'blue'};
  return known || {icon: CircleDot, tone: 'slate'};
};

/** A system-generated entry has no human actor (created_by is null/absent). */
const isSystemEntry = (entry: HistoryEntry): boolean =>
  entry.action_type === 'email_sent' || !entry.created_by;

const actionLabel = (entry: HistoryEntry): string =>
  entry.action_description ||
  (entry.action_type ? ACTION_LABELS[entry.action_type] : undefined) ||
  entry.action ||
  'Activity';

const initialsOf = (name?: string | null): string => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  return parts.slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');
};

const humanizeField = (field: string): string =>
  field
    .replace(/_/g, ' ')
    .replace(/\bid\b/i, '')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());

/**
 * `timeZone` renders timestamp values in a specific zone with its abbreviation,
 * so a logged old/new time reads in the same zone as the screen that changed it
 * (e.g. a session's timing, entered in the session's timezone) instead of the
 * viewer's. Without it, values stay in the viewer's local zone as before.
 */
const formatValue = (v: string | number | null, timeZone?: string): string => {
  if (v === null || v === undefined || v === '') return 'None';
  if (typeof v === 'string') {
    const d = new Date(v);
    if (/\d{4}-\d{2}-\d{2}T/.test(v) && !Number.isNaN(d.getTime())) {
      try {
        return d.toLocaleString('en-US', {
          month: 'short',
          day: '2-digit',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          ...(timeZone ? {timeZone, timeZoneName: 'short'} : {}),
        });
      } catch {
        return d.toLocaleString('en-US');
      }
    }
  }
  return String(v);
};

const dayKey = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? 'unknown' : d.toDateString();
};

const dayLabel = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return 'Unknown date';
  return d
    .toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: '2-digit',
      year: 'numeric',
    })
    .toUpperCase();
};

const timeLabel = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-US', {hour: '2-digit', minute: '2-digit'});
};

const absoluteLabel = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('en-US', {
    weekday: 'short',
    month: 'short',
    day: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/** Email delivery-status pill (email_sent entries). */
const DELIVERY_TONE: Record<string, string> = {
  delivered:
    'border-emerald-200 text-emerald-700 dark:border-emerald-500/30 dark:text-emerald-400',
  sent: 'border-border text-muted-foreground',
  bounced: 'border-rose-200 text-rose-700 dark:border-rose-500/30 dark:text-rose-400',
  failed: 'border-rose-200 text-rose-700 dark:border-rose-500/30 dark:text-rose-400',
};

const ChangeChip = ({
  change,
  timeZone,
}: Readonly<{change: HistoryChange; timeZone?: string}>) => (
  <div className="flex flex-wrap items-center gap-1.5 text-xs">
    <span className="text-muted-foreground">{humanizeField(change.field)}:</span>
    <span className="rounded-md bg-muted px-2 py-0.5 font-medium text-foreground">
      {formatValue(change.from, timeZone)}
    </span>
    <span className="text-muted-foreground">→</span>
    <span className="rounded-md bg-primary/10 px-2 py-0.5 font-medium text-foreground">
      {formatValue(change.to, timeZone)}
    </span>
  </div>
);

const HistoryRow = ({
  entry,
  isLast,
  timeZone,
}: Readonly<{entry: HistoryEntry; isLast: boolean; timeZone?: string}>) => {
  const system = isSystemEntry(entry);
  const actor = system ? 'System' : entry.created_by_name || 'Unknown user';
  const changes = entry.metadata?.changes ?? [];
  const reason = entry.metadata?.reason;
  // The "subject" badge: whichever entity this entry is about (session / team
  // member / email template) — only one of these is ever set on a given entry.
  const sessionName =
    entry.metadata?.session_name ??
    entry.metadata?.member_name ??
    entry.metadata?.template_label ??
    // Integration-authored events name their origin here instead of a subject,
    // so a System entry says which system produced it.
    entry.metadata?.source_label;
  const deliveryStatus = entry.metadata?.delivery_status;
  const bulkUploadCount = entry.metadata?.bulk_upload_count;
  const detail = entry.details && entry.details !== reason ? entry.details : null;
  const {icon: Icon, tone} = actionVisual(entry);

  return (
    <div className="relative flex gap-3 pb-5 last:pb-0">
      {/* Timeline connector rail (hidden on the last node of a day group). */}
      {!isLast && (
        <span
          aria-hidden
          className="absolute left-4 top-9 bottom-0 w-px -translate-x-1/2 bg-border"
        />
      )}

      {/* Colour-coded action node. */}
      <div
        className={`relative z-10 flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${TONE_CLASS[tone]}`}
      >
        <Icon className="h-4 w-4" />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <span className="text-sm font-semibold leading-tight text-foreground">
            {actionLabel(entry)}
          </span>
          <span
            className="shrink-0 text-xs text-muted-foreground"
            title={absoluteLabel(entry.created_at)}
          >
            {timeLabel(entry.created_at)}
          </span>
        </div>

        {/* Actor + context line. */}
        <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
          {system ? (
            <span className="inline-flex items-center gap-1">
              <Settings className="h-3 w-3" />
              System
            </span>
          ) : (
            <span className="inline-flex items-center gap-1.5">
              <Avatar className="h-5 w-5">
                <AvatarFallback className="bg-blue-600 text-[9px] font-medium text-white">
                  {initialsOf(entry.created_by_name)}
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{actor}</span>
              {entry.created_by_role && (
                <span className="text-muted-foreground">
                  ({entry.created_by_role})
                </span>
              )}
            </span>
          )}
          {sessionName && (
            <Badge variant="secondary" className="h-5 px-1.5 text-[11px] font-medium">
              {sessionName}
            </Badge>
          )}
          {deliveryStatus && (
            <Badge
              variant="outline"
              className={`h-4 px-1.5 text-[10px] font-medium capitalize ${
                DELIVERY_TONE[String(deliveryStatus).toLowerCase()] ??
                'border-border text-muted-foreground'
              }`}
            >
              {deliveryStatus}
            </Badge>
          )}
        </div>

        {changes.length > 0 && (
          <div className="mt-1.5 space-y-1">
            {changes.map((c, i) => (
              <ChangeChip key={`${c.field}-${i}`} change={c} timeZone={timeZone} />
            ))}
          </div>
        )}
        {reason && (
          <p className="mt-1.5 rounded-md border-l-2 border-border bg-muted/40 px-2.5 py-1.5 text-xs text-muted-foreground">
            {reason}
          </p>
        )}
        {detail && <p className="mt-1 text-xs text-muted-foreground">{detail}</p>}
        {bulkUploadCount != null && (
          <p className="mt-1 text-xs text-muted-foreground">
            {bulkUploadCount} template{bulkUploadCount === 1 ? '' : 's'} configured
          </p>
        )}
      </div>
    </div>
  );
};

const FilterChip = ({
  active,
  label,
  count,
  onClick,
}: Readonly<{
  active: boolean;
  label: string;
  count: number;
  onClick: () => void;
}>) => (
  <button
    type="button"
    onClick={onClick}
    className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium transition-colors ${
      active
        ? 'bg-foreground text-background'
        : 'bg-muted text-muted-foreground hover:bg-muted/70'
    }`}
  >
    {label}
    <span
      className={`rounded-full px-1.5 text-[10px] ${
        active ? 'bg-background/20' : 'bg-background/60'
      }`}
    >
      {count}
    </span>
  </button>
);

const matchesActorFilter = (
  entry: HistoryEntry,
  filter: ActorFilter,
): boolean => {
  if (filter === 'system') return isSystemEntry(entry);
  if (filter === 'users') return !isSystemEntry(entry);
  return true;
};

type DayGroup = {key: string; label: string; items: HistoryEntry[]};

// Newest-first, then grouped into day buckets (preserving that order).
const groupEntriesByDay = (entries: HistoryEntry[]): DayGroup[] => {
  const ordered = [...entries].sort(
    (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
  );
  const groups: DayGroup[] = [];
  for (const entry of ordered) {
    const key = dayKey(entry.created_at);
    const last = groups[groups.length - 1];
    if (last && last.key === key) {
      last.items.push(entry);
    } else {
      groups.push({key, label: dayLabel(entry.created_at), items: [entry]});
    }
  }
  return groups;
};

export default function HistoryTimeline({
  entries,
  isLoading = false,
  isError = false,
  mode = 'participant',
  emptyLabel,
  showFilter = true,
  timeZone,
}: Readonly<HistoryTimelineProps>) {
  const [filter, setFilter] = React.useState<ActorFilter>('all');

  if (isLoading) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">Loading history…</p>
    );
  }
  if (isError) {
    return (
      <p className="py-8 text-center text-sm text-destructive">
        Couldn’t load history. Please try again.
      </p>
    );
  }
  if (!entries || entries.length === 0) {
    return (
      <p className="py-8 text-center text-sm text-muted-foreground">
        {emptyLabel ??
          (mode === 'session'
            ? 'No history for this session yet.'
            : 'No history yet.')}
      </p>
    );
  }

  const systemCount = entries.filter(isSystemEntry).length;
  const counts = {
    all: entries.length,
    system: systemCount,
    users: entries.length - systemCount,
  };

  const visible = entries.filter((e) => matchesActorFilter(e, filter));
  const groups = groupEntriesByDay(visible);

  return (
    <div className="space-y-4">
      {showFilter && (
        <div className="flex flex-wrap items-center gap-2">
          <FilterChip
            active={filter === 'all'}
            label="All"
            count={counts.all}
            onClick={() => setFilter('all')}
          />
          <FilterChip
            active={filter === 'users'}
            label="By Users"
            count={counts.users}
            onClick={() => setFilter('users')}
          />
          <FilterChip
            active={filter === 'system'}
            label="By System"
            count={counts.system}
            onClick={() => setFilter('system')}
          />
        </div>
      )}

      {groups.length === 0 ? (
        <p className="py-8 text-center text-sm text-muted-foreground">
          {filter === 'system'
            ? 'No system actions yet.'
            : 'No user actions yet.'}
        </p>
      ) : (
        <div className="space-y-5">
          {groups.map((group) => (
            <div key={group.key}>
              <div className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                {group.label}
              </div>
              <div>
                {group.items.map((entry, i) => (
                  <HistoryRow
                    key={entry.id}
                    entry={entry}
                    isLast={i === group.items.length - 1}
                    timeZone={timeZone}
                  />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
